import { useEffect } from "react";
import { trimDescription } from "@/lib/meta";

/**
 * Per-route title, description and canonical.
 *
 * Every page already set document.title by hand; none set a description, so all
 * eight routes shared the one sentence in index.html and competed for the same
 * snippet. This does both, plus the canonical, from one call.
 *
 * What it does NOT do is fix link previews. The crawlers behind those — Slack,
 * Discord, Facebook, LinkedIn, iMessage — fetch the HTML and never execute a
 * line of JavaScript, so they see index.html and nothing this file writes
 * afterwards.
 *
 * That is now handled where it has to be, before the response leaves the CDN:
 * netlify/edge-functions/report-preview.js rewrites the same tags for
 * /report/:id, which is the route with artwork worth unfurling. Its description
 * is the same sentence composeDescription() builds here, kept that way by
 * src/__tests__/meta-contract.test.js. Every other route unfurls with
 * index.html's own tags and its 1200x630 board.
 *
 * The og: values here are still written regardless, because Google does render
 * the page and does read them.
 */

const SITE = "https://slantscan.com";

/**
 * Every tag this hook touches, so the ones a route does not set can be put back
 * the way index.html had them. See restoreMeta.
 */
const SHARE_IMAGE = ['meta[property="og:image"]', 'meta[name="twitter:image"]'];
const SHARE_TYPE = 'meta[property="og:type"]';
const SHARE_CARD = 'meta[name="twitter:card"]';

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    // Marked so restoreMeta can tell "index.html shipped this and we changed
    // it" from "index.html never had this and we invented it". The first gets
    // its original value back; the second has no original and is removed.
    el.setAttribute("data-meta-created", "1");
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Write a tag's content, remembering on the element what index.html said first.
 *
 * The original is stashed in the DOM rather than in a module-level map on
 * purpose: it survives a hot reload, it needs no reset hook in tests, and there
 * is exactly one of it per tag no matter how many components call this.
 */
function setContent(el, content) {
  if (!el.hasAttribute("data-meta-created") && !el.hasAttribute("data-meta-default")) {
    el.setAttribute("data-meta-default", el.getAttribute("content") ?? "");
  }
  el.setAttribute("content", content);
}

/** Put a tag back to index.html's value, or remove it if it had none. */
function restoreMeta(selector) {
  const el = document.head.querySelector(selector);
  if (!el) return;
  if (el.hasAttribute("data-meta-created")) {
    el.remove();
    return;
  }
  const original = el.getAttribute("data-meta-default");
  if (original != null) el.setAttribute("content", original);
}

function setMetaByName(name, content) {
  setContent(
    upsert(`meta[name="${name}"]`, () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    }),
    content,
  );
}

function setMetaByProperty(property, content) {
  setContent(
    upsert(`meta[property="${property}"]`, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", property);
      return m;
    }),
    content,
  );
}

/**
 * @param {string} title
 * @param {string} description
 * @param {{image?: string, type?: string, card?: string}} [share]
 *   Optional per-route sharing overrides. A report is the only route that has
 *   artwork of its own, and it used to write these nine tags by hand from a
 *   local copy of the helpers below — two implementations of one job, and the
 *   hand-written one silently missed the canonical. Passing them here instead
 *   means every route goes through one code path, and a route with nothing to
 *   override gets index.html's defaults put back.
 */
export function usePageMeta(title, description, share) {
  // Destructured out of the object so the effect can depend on three primitives
  // rather than an object literal, which is a new identity on every render and
  // would re-run this on every keystroke of any page holding input state.
  const shareImage = share?.image;
  const shareType = share?.type;
  const shareCard = share?.card;

  // Read during render, not inside the effect. window.location is not reactive,
  // so a value read inside the effect cannot be a dependency of it — and it has
  // to be one. /legal, /terms and /privacy are one component with one constant
  // title, so with the path missing from the deps below the effect never re-ran
  // across them and all three kept the canonical and og:url of whichever was
  // opened first. React re-renders on every navigation, so reading here is
  // always the path currently on screen.
  const pathname = typeof window === "undefined" ? "/" : window.location.pathname;

  useEffect(() => {
    if (title) {
      document.title = title;
      setMetaByProperty("og:title", title);
      setMetaByName("twitter:title", title);
    }

    if (description) {
      const trimmed = trimDescription(description);
      setMetaByName("description", trimmed);
      setMetaByProperty("og:description", trimmed);
      setMetaByName("twitter:description", trimmed);
    }

    // Artwork and card type, for the routes that have their own — and put back
    // for the routes that don't.
    //
    // These used to be written when present and never cleared, on the reasoning
    // that clearing them would leave a bare link. It did the opposite: a report
    // sets og:image to its own slant card, so every route visited after any
    // report unfurled carrying that unrelated report's artwork, for the rest of
    // the session. Restoring index.html's site-wide board is what "fall back to
    // the default" actually requires.
    if (shareImage) {
      setMetaByProperty("og:image", shareImage);
      setMetaByName("twitter:image", shareImage);
    } else {
      SHARE_IMAGE.forEach(restoreMeta);
    }

    if (shareType) setMetaByProperty("og:type", shareType);
    else restoreMeta(SHARE_TYPE);

    if (shareCard) setMetaByName("twitter:card", shareCard);
    else restoreMeta(SHARE_CARD);

    const url = `${SITE}${pathname}`;
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }).setAttribute("href", url);
    setMetaByProperty("og:url", url);
  }, [title, description, shareImage, shareType, shareCard, pathname]);
}
